import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';


const EquipmentPerformanceAnalytics = ({ equipment, performanceData }) => {
  const [selectedEquipment, setSelectedEquipment] = useState(equipment?.[0]?.id || '');
  const [metricType, setMetricType] = useState('uptime'); // uptime, accuracy, efficiency
  const [timeRange, setTimeRange] = useState('30days');

  const getPerformanceMetrics = useMemo(() => {
    const selectedData = performanceData?.filter(data => 
      selectedEquipment === '' || data?.equipmentId === selectedEquipment
    );

    const now = new Date();
    const days = timeRange === '7days' ? 7 : timeRange === '30days' ? 30 : 90;
    const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    return selectedData?.filter(data => new Date(data.timestamp) >= cutoffDate)?.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [performanceData, selectedEquipment, timeRange]);

  const aggregatedMetrics = useMemo(() => {
    const metrics = getPerformanceMetrics;
    if (metrics?.length === 0) return [];

    const groupedByDay = {};
    metrics?.forEach(metric => {
      const day = metric?.timestamp?.split('T')?.[0];
      if (!groupedByDay?.[day]) {
        groupedByDay[day] = {
          date: day,
          uptime: [],
          accuracy: [],
          efficiency: [],
          errorCount: 0,
          totalReadings: 0
        };
      }
      
      groupedByDay?.[day]?.uptime?.push(metric?.uptime);
      groupedByDay?.[day]?.accuracy?.push(metric?.accuracy);
      groupedByDay?.[day]?.efficiency?.push(metric?.efficiency);
      groupedByDay[day].errorCount += metric?.errorCount || 0;
      groupedByDay[day].totalReadings += 1;
    });

    return Object.values(groupedByDay)?.map(day => ({
      date: new Date(day.date)?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
      uptime: day?.uptime?.reduce((a, b) => a + b, 0) / day?.uptime?.length,
      accuracy: day?.accuracy?.reduce((a, b) => a + b, 0) / day?.accuracy?.length,
      efficiency: day?.efficiency?.reduce((a, b) => a + b, 0) / day?.efficiency?.length,
      errorRate: (day?.errorCount / day?.totalReadings) * 100,
      readings: day?.totalReadings
    }));
  }, [getPerformanceMetrics]);

  const performanceSummary = useMemo(() => {
    const metrics = getPerformanceMetrics;
    if (metrics?.length === 0) return null;

    const avgUptime = metrics?.reduce((sum, m) => sum + m?.uptime, 0) / metrics?.length;
    const avgAccuracy = metrics?.reduce((sum, m) => sum + m?.accuracy, 0) / metrics?.length;
    const avgEfficiency = metrics?.reduce((sum, m) => sum + m?.efficiency, 0) / metrics?.length;
    const totalErrors = metrics?.reduce((sum, m) => sum + (m?.errorCount || 0), 0);
    const errorRate = (totalErrors / metrics?.length) * 100;

    // Calculate trends (last 7 days vs previous 7 days)
    const recent = metrics?.slice(-7);
    const previous = metrics?.slice(-14, -7);
    
    const recentUptime = recent?.reduce((sum, m) => sum + m?.uptime, 0) / recent?.length;
    const previousUptime = previous?.length > 0 ? previous?.reduce((sum, m) => sum + m?.uptime, 0) / previous?.length : recentUptime;
    const uptimeTrend = ((recentUptime - previousUptime) / previousUptime) * 100;

    return {
      avgUptime,
      avgAccuracy,
      avgEfficiency,
      errorRate,
      uptimeTrend,
      totalReadings: metrics?.length
    };
  }, [getPerformanceMetrics]);

  const getMetricColor = (value, type) => {
    switch (type) {
      case 'uptime':
        return value >= 95 ? 'text-success' : value >= 85 ? 'text-warning' : 'text-error';
      case 'accuracy':
        return value >= 98 ? 'text-success' : value >= 95 ? 'text-warning' : 'text-error';
      case 'efficiency':
        return value >= 90 ? 'text-success' : value >= 80 ? 'text-warning' : 'text-error';
      default:
        return 'text-foreground';
    }
  };

  const getStatusIndicator = (value, type) => {
    const colorClass = getMetricColor(value, type);
    const iconName = colorClass?.includes('success') ? 'TrendingUp' : 
                    colorClass?.includes('warning') ? 'Minus' : 'TrendingDown';
    return { colorClass, iconName };
  };

  const selectedEquipmentData = equipment?.find(eq => eq?.id === selectedEquipment);

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Performance Analytics</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Equipment performance trends and efficiency metrics
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <select
              value={selectedEquipment}
              onChange={(e) => setSelectedEquipment(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            >
              <option value="">All Equipment</option>
              {equipment?.map(eq => (
                <option key={eq?.id} value={eq?.id}>
                  {eq?.deviceId} - {eq?.name}
                </option>
              ))}
            </select>
            
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Performance Summary Cards */}
        {performanceSummary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Average Uptime</p>
                  <p className={`text-2xl font-bold ${getMetricColor(performanceSummary?.avgUptime, 'uptime')}`}>
                    {performanceSummary?.avgUptime?.toFixed(1)}%
                  </p>
                  <p className={`text-xs flex items-center ${performanceSummary?.uptimeTrend >= 0 ? 'text-success' : 'text-error'}`}>
                    <Icon 
                      name={performanceSummary?.uptimeTrend >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                      size={12} 
                      className="mr-1" 
                    />
                    {Math.abs(performanceSummary?.uptimeTrend)?.toFixed(1)}% vs last period
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Activity" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-success/5 rounded-lg p-4 border border-success/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Average Accuracy</p>
                  <p className={`text-2xl font-bold ${getMetricColor(performanceSummary?.avgAccuracy, 'accuracy')}`}>
                    {performanceSummary?.avgAccuracy?.toFixed(1)}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Target" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-warning/5 rounded-lg p-4 border border-warning/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Efficiency</p>
                  <p className={`text-2xl font-bold ${getMetricColor(performanceSummary?.avgEfficiency, 'efficiency')}`}>
                    {performanceSummary?.avgEfficiency?.toFixed(1)}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-error/5 rounded-lg p-4 border border-error/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Error Rate</p>
                  <p className="text-2xl font-bold text-foreground">
                    {performanceSummary?.errorRate?.toFixed(2)}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {performanceSummary?.totalReadings} total readings
                  </p>
                </div>
                <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-error" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Equipment Details */}
        {selectedEquipmentData && (
          <div className="bg-muted/20 rounded-lg p-4 mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-foreground">{selectedEquipmentData?.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedEquipmentData?.deviceId} • {selectedEquipmentData?.department} • {selectedEquipmentData?.model}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Status</p>
                <div className={`inline-flex items-center space-x-2 px-2 py-1 rounded-full ${
                  selectedEquipmentData?.status === 'operational' ? 'bg-success/10 text-success' :
                  selectedEquipmentData?.status === 'maintenance'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                }`}>
                  <Icon 
                    name={selectedEquipmentData?.status === 'operational' ? 'CheckCircle' : 
                          selectedEquipmentData?.status === 'maintenance' ? 'Clock' : 'AlertTriangle'} 
                    size={12} 
                  />
                  <span className="text-xs font-medium capitalize">
                    {selectedEquipmentData?.status?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Uptime Trend */}
          <div className="bg-muted/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">Uptime Trend</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span className="text-sm text-muted-foreground">Uptime %</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={aggregatedMetrics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    domain={[80, 100]}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value?.toFixed(1)}%`, 'Uptime']}
                    labelStyle={{ color: 'var(--color-foreground)' }}
                    contentStyle={{ 
                      backgroundColor: 'var(--color-popover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="uptime" 
                    stroke="#1E40AF" 
                    fill="#1E40AF" 
                    fillOpacity={0.1}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Accuracy vs Efficiency */}
          <div className="bg-muted/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">Accuracy vs Efficiency</h3>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-success rounded-full"></div>
                  <span className="text-sm text-muted-foreground">Accuracy</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-warning rounded-full"></div>
                  <span className="text-sm text-muted-foreground">Efficiency</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={aggregatedMetrics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    domain={[85, 100]}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value?.toFixed(1)}%`, name]}
                    labelStyle={{ color: 'var(--color-foreground)' }}
                    contentStyle={{ 
                      backgroundColor: 'var(--color-popover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="accuracy" 
                    stroke="#059669" 
                    strokeWidth={2}
                    name="Accuracy"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="efficiency" 
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    name="Efficiency"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Performance Insights */}
        <div className="mt-8 bg-muted/20 rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Performance Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center space-x-3">
                <Icon name="TrendingUp" size={20} className="text-success" />
                <div>
                  <p className="text-sm font-medium text-foreground">Best Performing Day</p>
                  <p className="text-xs text-muted-foreground">
                    {aggregatedMetrics?.length > 0 && 
                      aggregatedMetrics?.reduce((best, current) => 
                        current?.uptime > best?.uptime ? current : best
                      )?.date
                    } - {aggregatedMetrics?.length > 0 && 
                      aggregatedMetrics?.reduce((best, current) => 
                        current?.uptime > best?.uptime ? current : best
                      )?.uptime?.toFixed(1)
                    }% uptime
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
                <div>
                  <p className="text-sm font-medium text-foreground">Attention Needed</p>
                  <p className="text-xs text-muted-foreground">
                    {aggregatedMetrics?.filter(m => m?.uptime < 90)?.length} days below 90% uptime
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center space-x-3">
                <Icon name="Target" size={20} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Accuracy Target</p>
                  <p className="text-xs text-muted-foreground">
                    {performanceSummary && performanceSummary?.avgAccuracy >= 98 ? 'Meeting' : 'Below'} 98% target
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentPerformanceAnalytics;